import { useAutoneTranslation } from '@autone/translations';
import { useMemo } from 'react';

import {
  type AdvancedFiltersOption,
  type AdvancedFiltersState,
} from '../../advanced-filters';
import {
  type GeneralFilterGroup,
  type SelectedGeneralFilters,
} from '../../general-filters';
import { type FilterChipType } from '../types';

import { FilterChip } from './FilterChip';
import {
  convertToAdvancedFilterChips,
  convertToGeneralFilterChips,
  type FilterChipsSelectedAdvancedFilters,
} from './FilterChips.helpers';

type GeneralProps = {
  generalFilterOptions: GeneralFilterGroup[];
  selectedGeneralFilters: SelectedGeneralFilters;
  handleSelectGeneralFilters?: (
    selectedGeneralFilters: SelectedGeneralFilters,
  ) => void;
};

type AdvancedProps<S extends FilterChipsSelectedAdvancedFilters> = {
  selectedAdvancedFilters: S | null;
  setSelectedAdvancedFilters: (state: S | null) => void;
  advancedFilterColumnOptions: AdvancedFiltersOption[];
};

type FilterChipsProps<S extends FilterChipsSelectedAdvancedFilters> = (
  | (AdvancedProps<S> & Partial<GeneralProps>)
  | (GeneralProps & Partial<AdvancedProps<S>>)
  | (GeneralProps & AdvancedProps<S>)
  // This type supports the saved filter templates modal where the setters are not passed
  | (Omit<GeneralProps, 'setSelectedGeneralFilters'> &
      Omit<AdvancedProps<S>, 'setSelectedAdvancedFilters'> & {
        setSelectedGeneralFilters?: undefined;
        setSelectedAdvancedFilters?: undefined;
      })
) & { includeWrapper?: boolean };

/**
 * TODO: I feel like rendering of general and advanced filter chips should be handled by separate components.
 */
export const FilterChips = <S extends AdvancedFiltersState>({
  generalFilterOptions,
  selectedGeneralFilters,
  handleSelectGeneralFilters,
  selectedAdvancedFilters,
  setSelectedAdvancedFilters,
  advancedFilterColumnOptions,
  includeWrapper = true,
}: FilterChipsProps<S>) => {
  const advancedFiltersIsPossible =
    selectedAdvancedFilters && advancedFilterColumnOptions;
  const generalFiltersIsPossible =
    generalFilterOptions && selectedGeneralFilters;

  // We don't show the option to remove filter chips for the saved filter templates modal
  const showX = !!handleSelectGeneralFilters || !!setSelectedAdvancedFilters;

  const { common } = useAutoneTranslation();
  const generalFilterChips: FilterChipType[] = useMemo(
    () =>
      generalFiltersIsPossible
        ? convertToGeneralFilterChips({
            generalFilterOptions,
            selectedGeneralFilters,
            common,
          })
        : [],
    [common, generalFilterOptions, selectedGeneralFilters],
  );

  const advancedFilterChips: FilterChipType[] = useMemo(
    () =>
      advancedFiltersIsPossible
        ? convertToAdvancedFilterChips({
            selectedAdvancedFilters,
            advancedFilterColumnOptions,
            common,
          })
        : [],
    [selectedAdvancedFilters, advancedFilterColumnOptions, common],
  );

  const handleGeneralFilterRemove = (filterId: string) => {
    const updatedGeneralFilters = {
      ...selectedGeneralFilters,
    };
    delete updatedGeneralFilters[filterId];
    handleSelectGeneralFilters?.(updatedGeneralFilters);
  };

  const handleAdvancedFilterRemove = (filterId: string) => {
    if (!selectedAdvancedFilters) setSelectedAdvancedFilters?.(null);
    else {
      const filteredConditions = selectedAdvancedFilters.conditions.filter(
        ({ filterId: id }) => id !== filterId,
      );
      setSelectedAdvancedFilters?.({
        ...selectedAdvancedFilters,
        conditions: filteredConditions ?? [],
      });
    }
  };

  if (!generalFilterChips.length && !advancedFilterChips.length) {
    return null;
  }

  const filterChipsToRender = [
    ...generalFilterChips.map(
      (
        { id, translatedFilterName, translatedCondition, translatedPostfix },
        index,
      ) => (
        <FilterChip
          key={index}
          id={id}
          filterName={translatedFilterName}
          filterCondition={translatedCondition}
          filterPostfix={translatedPostfix}
          onFilterRemove={handleGeneralFilterRemove}
          showX={showX}
        />
      ),
    ),
    ...advancedFilterChips.map(
      (
        { id, translatedFilterName, translatedCondition, translatedPostfix },
        index,
      ) => (
        <FilterChip
          key={index}
          id={id}
          filterName={translatedFilterName}
          filterCondition={translatedCondition}
          filterPostfix={translatedPostfix}
          onFilterRemove={handleAdvancedFilterRemove}
          showX={showX}
        />
      ),
    ),
  ];

  if (includeWrapper) {
    return <div className="flex gap-2 flex-wrap">{filterChipsToRender}</div>;
  } else {
    return filterChipsToRender;
  }
};
